import { useEffect, useRef } from 'react'
import styles from './GrantFinderBanner.module.scss'
import BannerBg from '@percent/cause-dashboard/assets/images/goodstack-pro/banner/bg.webp'
import BannerGraphic from '@percent/cause-dashboard/assets/images/goodstack-pro/banner/maia.webp'
import {
  POSTHOG_FEATURE_FLAGS,
  useAuthState,
  useFeatureFlagsReady,
  useCausesPortalAnalytics,
  usePostHogFeatureFlagPayload,
  usePostHogFeatureFlagVariant
} from '@percent/cause-dashboard/common/hooks'
import type { GrantFinderBannerCopyPayload } from '@percent/cause-dashboard/common/hooks'
import { Box, Button, Text } from '@percent/lemonade'
import { useNavigate } from 'react-router-dom'
import { RoutePath } from '../../routes/Routes'
import { createChatTargets } from '@percent/cause-dashboard/services/aichat/aichat'
import { PageViewSource } from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/marketplaceAnalytics.types'
import { useTranslation } from 'react-i18next'

const GRANT_FINDER_BANNER_UTM_SOURCE = 'cp-homepage-banner'

export const GrantFinderBanner = () => {
  const navigate = useNavigate()
  const { authState } = useAuthState()
  const { track } = useCausesPortalAnalytics()
  const { t } = useTranslation()
  const areFlagsReady = useFeatureFlagsReady()
  const copy = usePostHogFeatureFlagPayload<GrantFinderBannerCopyPayload>(
    POSTHOG_FEATURE_FLAGS.CP_GRANT_FINDER_BANNER_COPY
  )
  const experimentCopy = usePostHogFeatureFlagPayload<GrantFinderBannerCopyPayload>(
    POSTHOG_FEATURE_FLAGS.CP_GRANT_FINDER_BANNER_COPY_EXPERIMENT
  )
  /*
    Read the variant key as well as the payload so the flag is evaluated even when the
    assigned variant carries no payload (the `control` arm), which is what registers the
    experiment exposure for that user.
  */
  const experimentVariant = usePostHogFeatureFlagVariant(POSTHOG_FEATURE_FLAGS.CP_GRANT_FINDER_BANNER_COPY_EXPERIMENT)

  const titleSmall = experimentCopy?.header || copy?.header || t('grantFinderBanner.titleSmall')
  const titleBig = experimentCopy?.description || copy?.description || t('grantFinderBanner.titleBig')
  const cta = experimentCopy?.cta || copy?.cta || t('grantFinderBanner.cta')

  const trackingProperties = {
    organisation: authState.organisation?.displayName,
    organisationId: authState.organisation?.id,
    source: PageViewSource.Home,
    accountId: authState.user?.id,
    copyExperimentVariant: experimentVariant
  }

  const trackedShown = useRef(false)

  /*
    The impression is the denominator for the copy experiment's CTR, so it has to carry the
    assigned variant. Wait for PostHog to resolve flags before firing, otherwise the first
    render reports an undefined variant and the event cannot be attributed to an arm.
  */
  useEffect(() => {
    if (!areFlagsReady || trackedShown.current) {
      return
    }

    track('Grant Finder Banner Shown', trackingProperties)
    trackedShown.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    areFlagsReady,
    track,
    experimentVariant,
    authState.organisation?.displayName,
    authState.organisation?.id,
    authState.user?.id
  ])

  const handleExploreClick = () => {
    track('Grant Finder Banner CTA clicked', trackingProperties)
    navigate(
      `${RoutePath.AI_GATEWAY}?target=${createChatTargets.goodstackGrants}&utm_source=${GRANT_FINDER_BANNER_UTM_SOURCE}`
    )
  }

  return (
    <Box className={styles.bannerContainer} onClick={handleExploreClick}>
      <img src={BannerBg} alt="Background" className={styles.backgroundImage} />

      <Box className={styles.contentWrapper}>
        <Box className={styles.leftContent}>
          <Text className={styles.titleSmall}>{titleSmall}</Text>
          <Text className={styles.heading}>{titleBig}</Text>
          <Box className={styles.buttonWrapper}>
            {/*
              The wrapper's onClick makes the whole banner clickable, but Lemonade's Button
              stops the click propagating, so the CTA needs its own handler.
            */}
            <Button variant="secondary" onClick={handleExploreClick} data-testid="grant-finder-banner-cta-button">
              {cta}
            </Button>
          </Box>
        </Box>

        <Box className={styles.rightContent}>
          <img src={BannerGraphic} alt="Your funding needs - Goodstack Pro" className={styles.illustration} />
        </Box>
      </Box>
    </Box>
  )
}
